import { ref, type ComputedRef, type Ref } from 'vue'
import type { CrosschainBuyViewState } from '@/features/crosschain-buy/buy-flow/view-state'
import type { SourceChainType } from '@/features/crosschain-buy/source-tokens'
import { getUserMessageForRawError } from '@/features/crosschain-buy/buy-flow/presentation/errors'
import {
  isFullyCompletedExecution,
  START_NEW_SWAP_WARNING,
  shouldConfirmStartingNewSwap,
} from '@/features/crosschain-buy/buy-flow/presentation/start-new-swap-guard'
import { ErrorClass, FlowStatus } from '@/features/crosschain-buy/domain/flow'

export interface UseBuySpottyHandlersDeps {
  uiError: Ref<string>
  state: Ref<CrosschainBuyViewState>
  needsTonReconnect: Ref<boolean> | ComputedRef<boolean>
  needsTonBackendReauth: Ref<boolean> | ComputedRef<boolean>
  sourceWalletAddress: Ref<string> | ComputedRef<string>
  sourceChainType: Ref<SourceChainType> | ComputedRef<SourceChainType>
  solanaBridgeProvider: Ref<'rango' | 'layerswap'>
  runContinue: () => Promise<void>
  cancelExecution: () => Promise<boolean>
  refreshRecentSwaps: () => void
  debouncedRequote: (force?: boolean) => void
  handleConnectSource: () => Promise<void> | void
  handleConnectTon: () => Promise<void> | void
  recordDiagnostic?: (event: string, data?: Record<string, unknown>) => void
  isPhantomDeeplinkSuspended?: (error: unknown) => boolean
  routeLocked?: Ref<boolean> | ComputedRef<boolean>
  evmChainMismatch?:
    | Ref<{ actualLabel: string; targetLabel: string } | null>
    | ComputedRef<{
        actualLabel: string
        targetLabel: string
      } | null>
  handleSwitchEvmChain?: () => Promise<void>
}

export function useBuySpottyHandlers(deps: UseBuySpottyHandlersDeps) {
  const {
    uiError,
    state,
    needsTonReconnect,
    needsTonBackendReauth,
    sourceWalletAddress,
    sourceChainType,
    solanaBridgeProvider,
    runContinue,
    cancelExecution,
    refreshRecentSwaps,
    debouncedRequote,
    handleConnectSource,
    handleConnectTon,
    recordDiagnostic = () => undefined,
    isPhantomDeeplinkSuspended = () => false,
    routeLocked,
    evmChainMismatch,
    handleSwitchEvmChain,
  } = deps

  const isFlowBusy = ref(false)
  let exitTask: Promise<boolean> | null = null

  const handleContinue = async () => {
    uiError.value = ''
    recordDiagnostic('buySpotty.continue.clicked', {
      status: state.value.status,
      tonPreparationStatus: state.value.tonPreparationStatus ?? 'idle',
      swapId: state.value.currentSwapId,
    })

    if (evmChainMismatch?.value && handleSwitchEvmChain) {
      await handleSwitchEvmChain()
      return
    }

    if (isFullyCompletedExecution(state.value)) {
      await handleCancel(true)
      return
    }

    if (
      state.value.status === FlowStatus.ERROR &&
      state.value.lastError === ErrorClass.SOLANA_TX_FAILED
    ) {
      await handleCancel(true)
      return
    }

    // Reconnect-to-finish path: bridge already delivered USDT to state.ton.address
    // but TonConnect dropped the session. Open TonConnect; the isConnected watch
    // in useCrosschainBuy auto-fires executeTonLeg when the same wallet is
    // re-approved, so no second tap on "Start Swap" is needed.
    if (needsTonReconnect.value) {
      await handleConnectTon()
      return
    }

    if (needsTonBackendReauth.value) {
      await handleConnectTon()
      return
    }

    if (!sourceWalletAddress.value) {
      await handleConnectSource()
      return
    }

    if (!state.value.ton.address) {
      await handleConnectTon()
      return
    }

    isFlowBusy.value = true

    try {
      await runContinue()
    } catch (error) {
      // Phantom deeplink "happy path": the wallet has navigated the tab away to
      // sign — not a failure. Surfacing anything here would replace the
      // RouteStepper "Processing…" state with a misleading red banner.
      if (isPhantomDeeplinkSuspended(error)) {
        return
      }
      const msg = error instanceof Error ? error.message : 'Transaction failed'
      if (msg === 'execution_cancelled') {
        // User cancelled via the Cancel button — state is already reset.
      } else if (msg.startsWith('quote_stale')) {
        uiError.value = 'Quote is outdated. Refresh quote and try again.'
      } else if (msg === 'wallet_signature_timeout') {
        uiError.value = 'Wallet did not respond. Please try again.'
      } else if (!state.value.lastError) {
        uiError.value = getUserMessageForRawError(error)
      } else {
        if (import.meta.env.DEV) console.error('[BuySpottyContent] Caught error:', msg)
      }
    } finally {
      isFlowBusy.value = false
      recordDiagnostic('buySpotty.continue.settled', {
        status: state.value.status,
        tonPreparationStatus: state.value.tonPreparationStatus ?? 'idle',
        swapId: state.value.currentSwapId,
      })
      refreshRecentSwaps()
    }
  }

  const runCancel = async (skipConfirmation: boolean): Promise<boolean> => {
    uiError.value = ''
    const abandoningCommittedRoute = shouldConfirmStartingNewSwap(state.value)
    if (
      !skipConfirmation &&
      abandoningCommittedRoute &&
      typeof window !== 'undefined' &&
      !window.confirm(START_NEW_SWAP_WARNING)
    ) {
      return false
    }
    isFlowBusy.value = true

    try {
      if (!(await cancelExecution())) return false
      isFlowBusy.value = false
      debouncedRequote(true)
      return true
    } catch (error) {
      uiError.value = abandoningCommittedRoute
        ? 'Could not cancel the remaining steps. The current swap is still available.'
        : 'Could not cancel the swap. Please try again.'
      if (import.meta.env.DEV) {
        console.error('[BuySpottyContent] Canonical exit failed:', error)
      }
      return false
    } finally {
      isFlowBusy.value = false
      refreshRecentSwaps()
    }
  }

  const handleCancel = (skipConfirmation = false): Promise<boolean> => {
    if (exitTask) return exitTask
    const task = runCancel(skipConfirmation).finally(() => {
      if (exitTask === task) exitTask = null
    })
    exitTask = task
    return task
  }

  const switchSolanaBridgeProvider = (provider: 'rango' | 'layerswap') => {
    uiError.value = ''
    if (routeLocked?.value) return
    if (sourceChainType.value !== 'solana') return

    // Always sync provider explicitly (even if it appears unchanged),
    // because quote.provider and selected provider can get out of sync.
    solanaBridgeProvider.value = provider

    if (state.value.quote) {
      debouncedRequote()
    }
  }

  return {
    isFlowBusy,
    handleContinue,
    handleCancel,
    switchSolanaBridgeProvider,
  }
}
